<template>
  <div class="contest-page" v-if="contest">
    <!-- 🎯 Contest Header -->
    <div class="contest-header">
      <h1 class="contest-title">{{ contest.title }}</h1>
      <p class="contest-theme">Theme: <strong>{{ contest.theme }}</strong></p>
      <p class="contest-description">{{ contest.description }}</p>
      <p class="contest-reward">🏆 Reward: {{ contest.reward }}</p>
      <p class="contest-dates">
        🗓 {{ formatDate(contest.start_date) }} → {{ formatDate(contest.end_date) }}
      </p>
    </div>

    <!-- 🧭 Action -->
    <div class="contest-action">
      <button
        v-if="!isLoggedIn"
        class="btn-primary"
        @click="goToRegister"
      >
        🚀 Start Now
      </button>

      <button
        v-else
        class="btn-primary"
        @click="handleJoin"
      >
        ✍️ Join Contest
      </button>
    </div>

    <!-- 📝 Popup for Story Submission -->
    <div v-if="showForm" class="popup-overlay" @click.self="showForm = false">
      <div class="popup-box">
        <h2>📝 Submit Your Story</h2>

        <input
          v-model="newStory.title"
          placeholder="Enter story title"
          class="input"
        />

        <select v-model="newStory.genre" class="input">
          <option disabled value="">Select Genre</option>
          <option>Poetry</option>
          <option>Love</option>
          <option>Romantic</option>
          <option>Drama</option>
          <option>Horror</option>
          <option>Fiction</option>
          <option>Fantasy</option>
        </select>

        <textarea
          v-model="newStory.content"
          placeholder="Write your story here..."
          class="textarea"
        ></textarea>

        <!-- ✅ My Story Checkbox (Green tick removed) -->
        <div class="my-story">
          <input type="checkbox" v-model="newStory.myStory" id="myStory" />
          <label for="myStory">This is my original story</label>
        </div>

        <div class="actions">
          <button class="btn-secondary" @click="showForm = false">Cancel</button>
          <button class="btn-primary" @click="handleAdBeforeSubmit">Submit</button>
        </div>
      </div>
    </div>

    <!-- 📺 Simple Ad Popup -->
    <div v-if="showAd" class="ad-overlay" @click.self="closeAd">
      <div class="ad-box">
        <h3>📺 Sponsored Ad</h3>
        <p>This ad helps us keep Mudra free for everyone ❤️</p>
        <button class="btn-primary" @click="continueAfterAd">Continue</button>
      </div>
    </div>
  </div>

  <div v-else class="loading">Loading contest details...</div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { supabase } from "@/supabase";

const route = useRoute();
const router = useRouter();
const contestId = route.params.id;

const contest = ref(null);
const isLoggedIn = ref(false);
const userId = ref(null);
const showForm = ref(false);
const showAd = ref(false);
const adAction = ref(null); // 'join' or 'submit'

const newStory = ref({
  title: "",
  genre: "",
  content: "",
  myStory: false,
});

onMounted(async () => {
  const { data } = await supabase.auth.getSession();
  const session = data?.session;
  isLoggedIn.value = !!session;
  userId.value = session?.user?.id || null;
  await loadContest();
});

async function loadContest() {
  const { data, error } = await supabase
    .from("contests")
    .select("*")
    .eq("id", contestId)
    .single();
  if (!error) contest.value = data;
}

// 🧭 When user clicks "Join Contest"
function handleJoin() {
  adAction.value = "join";
  showAd.value = true;
}

// 📺 Ad handling
function handleAdBeforeSubmit() {
  adAction.value = "submit";
  showAd.value = true;
}
function closeAd() {
  showAd.value = false;
}
function continueAfterAd() {
  showAd.value = false;
  if (adAction.value === "join") showForm.value = true;
  if (adAction.value === "submit") submitStory();
}

// 📝 Submit Story
async function submitStory() {
  if (!newStory.value.title || !newStory.value.content || !newStory.value.myStory) {
    return alert("Please complete all fields and confirm it's your story ✅");
  }

  const { data: storyData, error: storyError } = await supabase
    .from("stories")
    .insert([
      {
        title: newStory.value.title,
        genre: newStory.value.genre,
        content: newStory.value.content,
        author_id: userId.value,
        status: "published",
      },
    ])
    .select()
    .single();

  if (storyError) {
    alert("Error creating story: " + storyError.message);
    return;
  }

  await supabase.from("contest_entries").insert([
    {
      contest_id: contestId,
      user_id: userId.value,
      story_id: storyData.id,
    },
  ]);

  alert("🎉 Story submitted successfully!");
  showForm.value = false;
  newStory.value = { title: "", genre: "", content: "", myStory: false };
}

function formatDate(date) {
  return new Date(date).toLocaleDateString();
}

function goToRegister() {
  router.push("/register");
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');

.contest-page {
  max-width: 900px;
  margin: 80px auto;
  padding: 25px;
  color: #eaf6ff;
  font-family: 'Inter', sans-serif;
}

/* 🌟 Contest Header */
.contest-header {
  background: rgba(0, 25, 50, 0.85);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(0, 200, 255, 0.3);
  backdrop-filter: blur(20px);
  text-align: center;
  box-shadow: 0 0 25px rgba(0, 200, 255, 0.25);
  animation: fadeIn 0.6s ease;
}
.contest-title {
  font-size: 32px;
  font-weight: 700;
  color: #00eaff;
  text-shadow: 0 0 12px rgba(0, 234, 255, 0.8);
}
.contest-theme {
  font-size: 17px;
  color: #aee0ff;
  margin-top: 8px;
}
.contest-description {
  margin-top: 12px;
  font-size: 15px;
  color: #dff6ff;
  line-height: 1.6;
}
.contest-reward {
  margin-top: 16px;
  font-size: 17px;
  color: #ffd700;
  font-weight: 600;
}
.contest-dates {
  font-size: 14px;
  color: #bdeaff;
  margin-top: 6px;
}

/* 🚀 Button */
.contest-action {
  display: flex;
  justify-content: center;
  margin-top: 25px;
}
.btn-primary {
  background: linear-gradient(90deg, #00eaff, #007bff);
  border: none;
  padding: 12px 28px;
  color: #001220;
  border-radius: 10px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: 0.3s ease;
  box-shadow: 0 0 15px rgba(0, 234, 255, 0.3);
}
.btn-primary:hover {
  transform: scale(1.08);
  box-shadow: 0 0 25px rgba(0, 234, 255, 0.6);
}

/* ✨ Popup Overlay */
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 15, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(10px);
}
.popup-box {
  background: rgba(0, 20, 40, 0.95);
  border: 1px solid rgba(0, 234, 255, 0.4);
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 520px;
  box-shadow: 0 0 40px rgba(0, 234, 255, 0.3);
  backdrop-filter: blur(25px);
  animation: popupGlow 0.4s ease;
}
.popup-box h2 {
  color: #00eaff;
  text-align: center;
  margin-bottom: 20px;
  font-size: 24px;
  text-shadow: 0 0 10px rgba(0, 234, 255, 0.6);
}

/* 🧊 Inputs */
.input, .textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(0, 234, 255, 0.3);
  color: #eaf6ff;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 14px;
  font-size: 15px;
  outline: none;
  transition: 0.3s ease;
}
.input:focus, .textarea:focus {
  border-color: #00eaff;
  box-shadow: 0 0 10px rgba(0, 234, 255, 0.5);
}
.textarea {
  min-height: 150px;
  resize: none;
}

/* ✅ Checkbox Stylish */
.my-story {
  display: flex;
  align-items: center;
  margin: 10px 0 20px;
}
.my-story input[type="checkbox"] {
  accent-color: #00eaff;
  transform: scale(1.2);
  cursor: pointer;
  transition: 0.2s;
}
.my-story label {
  margin-left: 10px;
  font-size: 15px;
  color: #aeefff;
}

/* Buttons */
.actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.btn-secondary {
  background: transparent;
  border: 1px solid rgba(0, 234, 255, 0.4);
  color: #00eaff;
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
  transition: 0.3s;
  font-weight: 600;
}
.btn-secondary:hover {
  background: rgba(0, 234, 255, 0.2);
  box-shadow: 0 0 10px rgba(0, 234, 255, 0.4);
}

/* 📺 Ad Popup */
.ad-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 20, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(12px);
}
.ad-box {
  background: rgba(10, 25, 50, 0.95);
  border-radius: 14px;
  border: 1px solid rgba(0, 234, 255, 0.4);
  padding: 25px;
  text-align: center;
  width: 340px;
  color: #eaf6ff;
  box-shadow: 0 0 25px rgba(0, 234, 255, 0.3);
  animation: popupGlow 0.4s ease;
}
.ad-box h3 {
  color: #00eaff;
  font-size: 20px;
  margin-bottom: 10px;
}
.ad-box p {
  font-size: 14px;
  color: #bdeaff;
  margin-bottom: 15px;
}

/* Animations */
@keyframes popupGlow {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 📱 Responsive */
@media (max-width: 600px) {
  .popup-box {
    padding: 20px;
  }
  .contest-title {
    font-size: 26px;
  }
}
</style>